import React, { useEffect } from 'react';
import { Globe, Code, Cpu, Coins, ArrowLeftRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { TemplateFormData } from '../TemplateWizard';

interface BasicInfoStepProps {
  data: TemplateFormData;
  onChange: (updates: Partial<TemplateFormData>) => void;
  onValidate: (isValid: boolean) => void;
}

const providerTypes = [
  { value: 'REST', label: 'REST API', icon: Globe, description: 'APIs HTTP tradicionais' },
  { value: 'GRAPHQL', label: 'GraphQL', icon: Code, description: 'APIs GraphQL' },
  { value: 'EVM', label: 'EVM Blockchain', icon: Cpu, description: 'Ethereum, Polygon, BSC' },
  { value: 'SOLANA', label: 'Solana', icon: Coins, description: 'Blockchain Solana' },
  { value: 'DEX', label: 'DEX', icon: ArrowLeftRight, description: 'Exchanges descentralizadas' },
] as const;

export const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  data,
  onChange,
  onValidate,
}) => {
  // Validate on data change
  useEffect(() => {
    const isValid = !!(
      data.name.trim() &&
      data.type &&
      data.version.trim() &&
      data.baseUrl.trim()
    );
    onValidate(isValid);
  }, [data.name, data.type, data.version, data.baseUrl, onValidate]);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Informações Básicas</h2>
        <p className="text-sm text-surface-400">
          Defina o nome, tipo e versão do template de provider
        </p>
      </div>

      {/* Name */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-surface-200">
          Nome do Template <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Ex: OpenPix, Asaas, Uniswap V3"
          className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
        />
      </div>

      {/* Type Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-surface-200">
          Tipo de Provider <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {providerTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = data.type === type.value;
            
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => onChange({ type: type.value as any })}
                className={cn(
                  'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                  isSelected
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-600 bg-surface-800 hover:border-surface-500'
                )}
              >
                <Icon className={cn(
                  'w-6 h-6',
                  isSelected ? 'text-primary-400' : 'text-surface-400'
                )} />
                <span className={cn(
                  'text-sm font-medium',
                  isSelected ? 'text-white' : 'text-surface-300'
                )}>
                  {type.label}
                </span>
                <span className="text-xs text-surface-500">{type.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Version */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-surface-200">
          Versão <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={data.version}
          onChange={(e) => onChange({ version: e.target.value })}
          placeholder="Ex: 1.0.0"
          className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
        />
        <p className="text-xs text-surface-500">Use versionamento semântico (major.minor.patch)</p>
      </div>

      {/* Base URL */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-surface-200">
          URL Base <span className="text-red-400">*</span>
        </label>
        <input
          type="url"
          value={data.baseUrl}
          onChange={(e) => onChange({ baseUrl: e.target.value })}
          placeholder="Ex: https://api.openpix.com.br/api/v1"
          className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
        />
        <p className="text-xs text-surface-500">URL base para todas as requisições da API</p>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-surface-200">
          Descrição
        </label>
        <textarea
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Descreva o provider e suas funcionalidades..."
          rows={3}
          className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 resize-none"
        />
      </div>
    </div>
  );
};

export default BasicInfoStep;
